'use client';

import { FallbackImage } from "@/components/common";
import { useEffect, useState } from "react";
import { ImageGallery } from "@/@types";

type Props = {
  fullscreenImage: string | null;
  fullscreenWidth: number;
  fullscreenHeight: number;
  imagesGallery: ImageGallery[] | null;
  currentIndex: number;
  closeModal: () => void;
  setCurrentIndex: (index: number) => void;
};

export default function FullscreenModal({
  fullscreenImage,
  fullscreenWidth,
  fullscreenHeight,
  imagesGallery,
  currentIndex,
  closeModal,
  setCurrentIndex
}: Props) {
  const [isLoading, setIsLoading] = useState(true);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (imagesGallery && imagesGallery.length > 0) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [imagesGallery]);

  const goToPrevious = () => {
    if (!imagesGallery || imagesGallery.length <= 1) return;
    setIsLoading(true);
    const prevIndex = currentIndex === 0 ? imagesGallery.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
  };

  const goToNext = () => {
    if (!imagesGallery || imagesGallery.length <= 1) return;
    setIsLoading(true);
    const nextIndex = currentIndex === imagesGallery.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(nextIndex);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!imagesGallery || imagesGallery.length <= 1) {
      if (e.key === "Escape") closeModal();
      return;
    }
    if (e.key === 'ArrowLeft') goToPrevious();
    if (e.key === 'ArrowRight') goToNext();
    if (e.key === 'Escape') closeModal();
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  useEffect(() => {
    setIsLoading(true);
  }, [currentIndex]);

  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 ||
        'ontouchstart' in window;
      setIsMobile(isMobileDevice);

      if (isMobileDevice) {
        setShowSwipeHint(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    if (isMobile && showSwipeHint) {
      const timer = setTimeout(() => {
        setShowSwipeHint(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isMobile, showSwipeHint]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
  };

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && imagesGallery && imagesGallery.length > 1) {
      goToNext();
      setShowSwipeHint(false);
    }
    if (isRightSwipe && imagesGallery && imagesGallery.length > 1) {
      goToPrevious();
      setShowSwipeHint(false);
    }
  };

  if (!imagesGallery || imagesGallery.length === 0) return null;

  const currentImage = imagesGallery[currentIndex];

  return (
    <div
      className="fixed inset-0 backdrop-blur-md bg-black bg-opacity-30 flex items-center justify-center z-50"
      onClick={closeModal}
    >
      <button
        className="absolute top-5 right-5 text-white text-4xl font-bold cursor-pointer z-10 hover:text-gray-300 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          closeModal();
        }}
      >
        ×
      </button>

      {imagesGallery.length > 1 && (
        <div className="absolute top-5 left-5 text-white text-lg bg-black bg-opacity-50 px-3 py-1 rounded z-10">
          {currentIndex + 1} / {imagesGallery.length}
        </div>
      )}

      {imagesGallery.length > 1 && (
        <button
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white text-6xl md:text-7xl lg:text-8xl hover:text-gray-300 transition-colors z-10 font-light"
          onClick={(e) => {
            e.stopPropagation();
            goToPrevious();
          }}
        >
          ‹
        </button>
      )}

      {imagesGallery.length > 1 && (
        <button
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white text-6xl md:text-7xl lg:text-8xl hover:text-gray-300 transition-colors z-10 font-light"
          onClick={(e) => {
            e.stopPropagation();
            goToNext();
          }}
        >
          ›
        </button>
      )}

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="bg-black bg-opacity-50 rounded-lg p-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        </div>
      )}

      {showSwipeHint && isMobile && imagesGallery.length > 1 && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg text-sm z-10 animate-pulse">
          Desliza para navegar
        </div>
      )}

      <div
        className="relative w-full h-full flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <FallbackImage
          src={currentImage?.src}
          alt={`Imagen ${currentIndex + 1} en pantalla completa`}
          fill
          className={`object-contain transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          sizes="100vw"
          onLoad={handleImageLoad}
          onError={handleImageError}
          key={currentIndex}
        />
      </div>
    </div>
  );
}
